import { formatFHIRDate } from '@ctw/shared/utils/dates';
import type { Claim } from 'fhir/r4';
import { codeableConceptLabel } from '../codeable-concept';
import { SYSTEM_CLAIM_TYPE, SYSTEM_CPT, SYSTEM_HCPCS, SYSTEM_ICD10_CM } from '../system-urls';
import { FHIRModel } from './fhir-model';

/**
 * Wraps a FHIR `Claim` for use as risk gap evidence. The extracted fields
 * mirror `summarizeClaim` from `@zeus-health/types` (which powers the card's
 * copy / add-to-note text), so the card and the summary stay consistent.
 */
export class ClaimModel extends FHIRModel<Claim> {
  public kind = 'Claim' as const;

  public get title(): string {
    return this.icd10Codes.length > 0 ? `ICD-10 ${this.icd10Codes.join(', ')}` : 'Claim';
  }

  /** ICD-10-CM codes from `diagnosis[].diagnosisCodeableConcept.coding`, deduplicated. */
  public get icd10Codes(): Array<string> {
    const seen = new Set<string>();
    for (const d of this.resource.diagnosis ?? []) {
      for (const c of d.diagnosisCodeableConcept?.coding ?? []) {
        if (c.system === SYSTEM_ICD10_CM && c.code) {
          seen.add(c.code);
        }
      }
    }
    return [...seen];
  }

  /** CPT (HCPCS Level I) + HCPCS Level II codes from `item[].productOrService.coding`, deduplicated. */
  public get serviceCodes(): Array<string> {
    const seen = new Set<string>();
    for (const item of this.resource.item ?? []) {
      for (const c of item.productOrService?.coding ?? []) {
        if ((c.system === SYSTEM_CPT || c.system === SYSTEM_HCPCS) && c.code) {
          seen.add(c.code);
        }
      }
    }
    return [...seen];
  }

  /**
   * Date of service: a `M/d/yyyy – M/d/yyyy` range when `billablePeriod.start`
   * and `.end` both exist and differ (e.g. inpatient stays), otherwise the
   * single date (`end`, falling back to `start`). Mirrors `summarizeClaim`.
   */
  public get serviceDate(): string | undefined {
    const start = this.resource.billablePeriod?.start
      ? formatFHIRDate(this.resource.billablePeriod.start)
      : undefined;
    const end = this.resource.billablePeriod?.end
      ? formatFHIRDate(this.resource.billablePeriod.end)
      : undefined;
    if (start && end && start !== end) {
      return `${start} – ${end}`;
    }
    return end ?? start;
  }

  /**
   * Claim type label from `Claim.type`, preferring the HL7 claim-type system's
   * `display` and falling back to the code (via `codeableConceptLabel`), e.g.
   * "Institutional" or "professional". The transformer writes the FHIR claim-type
   * vocabulary here (distinct from the NCH code in an extension that risk
   * eligibility uses).
   */
  public get claimType(): string | undefined {
    return codeableConceptLabel(this.resource.type, SYSTEM_CLAIM_TYPE) || undefined;
  }
}
